<!-- eslint-disable vue/no-v-html -->
<template>
  <article>
    <div class="flex flex-col items-center px-8 py-4">
      <div class="grid items-center w-full grid-cols-1 md:grid-cols-4">
        <div class="flex pb-2 pr-2 md:pb-0 capitalize-first md:block">
          <div class="text-sm text-gray-900 capitalize-first">
            {{ event.startAt.toDate() | date("dddd, D MMM") }}
          </div>
          <div class="text-sm text-gray-500">
            <span v-if="event.video">{{ "Recording" | i18n }}</span>
            <span v-else>{{ event.startAt.toDate() | date("HH:mm") }}</span>
            • {{ event.duration }}
            {{ "Min" | i18n }}
          </div>
        </div>
        <div class="flex items-center md:col-span-3">
          <div class="">
            {{ event.name }}
          </div>
          <div
            v-if="showBook"
            class="ml-auto"
          >
            <ButtonPrimary
              class="text-xs"
              @click-primary="bookFreeEvent(event)"
            >
              {{ "Button_Book" | i18n }}
            </ButtonPrimary>
          </div>
          <div
            v-else-if="foldableList"
            class="ml-auto text-gray-500 cursor-pointer hover:text-gray-600"
            :class="{ 'transform rotate-180': !folded }"
            @click.prevent="folded = !folded"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-1 opacity-0"
      >
        <div
          v-if="!folded"
          class="flex flex-col w-full"
        >
          <div class="mt-4 space-y-4">
            <div
              v-if="event.desc"
              id="editor"
              v-html="event.desc"
            />
            <div
              v-if="event.video"
              class="aspect-w-16 aspect-h-9"
            >
              <iframe
                width="560"
                height="315"
                :src="
                  `https://www.youtube-nocookie.com/embed/${$options.filters.youtubeId(
                    event.video
                  )}`
                "
                title="Video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              />
            </div>
            <div
              class="items-center w-full py-2 space-y-2 md:flex md:space-y-0 md:space-x-4"
            >
              <ButtonPrimary
                v-if="event.zoom"
                @click-primary="openZoom(event.zoom)"
              >
                {{ "Button_OpenZoom" | i18n }}
              </ButtonPrimary>
              <ButtonSecondary @click-secondary="showForm = !showForm">
                {{ "Button_LeaveReview" | i18n }}
              </ButtonSecondary>
            </div>
            <form
              v-if="showForm"
              novalidate
              class="relative z-0 w-full mb-3 transition duration-300 ease-in-out"
              @submit.prevent="submitFeedback(event)"
            >
              <textarea
                v-model="message"
                name="message"
                placeholder=" "
                rows="1"
                class="block w-full px-0 mt-0 bg-transparent border-0 border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-black"
                :class="
                  $v.message.$error ? 'border-red-600' : 'border-gray-200'
                "
              />
              <label
                for="message"
                class="absolute duration-300 top-3 -z-1 origin-0"
                :class="$v.message.$error ? 'text-red-600' : 'text-gray-500'"
              >
                {{ "InputLabel_EnterMessage" | i18n }}
              </label>
              <span
                v-if="$v.message.$dirty && !$v.message.required"
                class="text-sm text-red-600"
              >
                {{ "InputError_EmptyMessage" | i18n }}
              </span>
              <button
                type="submit"
                class="block px-6 py-1 mt-2 mb-1 text-center text-white transition-all duration-150 ease-linear border rounded-lg shadow outline-none bg-fs-500 border-fs-500 hover:bg-fs-600 hover:border-fs-600 hover:shadow-lg focus:outline-none"
              >
                {{ "Button_Send" | i18n }}
              </button>
            </form>
          </div>
        </div>
      </transition>
    </div>
  </article>
</template>

<script>
import { mapActions } from "vuex";
import { required } from "vuelidate/lib/validators";

import ButtonPrimary from "@/components/BaseButtonPrimary.vue";
import ButtonSecondary from "@/components/BaseButtonSecondary.vue";

export default {
  name: "ScheduleItem",

  components: { ButtonPrimary, ButtonSecondary },

  filters: {
    youtubeId(url) {
      const re = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/i;
      return url.match(re)[1];
    }
  },

  props: {
    event: {
      type: Object,
      default: () => {}
    },
    showBook: {
      type: Boolean,
      default: false
    },
    foldableList: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    folded: true,
    showForm: false,
    message: ""
  }),

  validations: {
    message: { required }
  },

  methods: {
    ...mapActions(["updateUser", "addEventAttendee", "logEvent", "submitForm"]),

    async bookFreeEvent(event) {
      try {
        await this.updateUser({
          freeEvent: {
            isActive: false,
            eventId: event.eventId,
            courseId: event.course.courseId,
            activatedAt: new Date()
          }
        });
        this.addEventAttendee(event.eventId);
        this.logEvent({ tagName: "freeEvent" });
        this.$router.push("/schedule");
      } catch (err) {
        this.$logger.log(err);
      }
    },

    async submitFeedback(event) {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }

      try {
        const eventKey = `
          ${event.name} 
          (${event.startAt.toDate()})
        `;
        await this.submitForm({
          type: "feedback",
          message: this.message,
          eventKey
        });
        this.showForm = false;
      } catch (err) {
        this.$logger.log(err);
      }
    },

    openZoom(url) {
      window.open(url, "_blank");
    }
  }
};
</script>

<style></style>
